import React, { useState, useEffect } from "react";
import axios from "axios";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { BasicForm } from "@components/shared";
import usStates from "@util/data/usStates";
import industries from "@util/data/industries";
import countries from "@lib/countries";

export default function OrganizationForm({ id, org, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  function onSubmit(formData) {
    setLoading(true);

    if (id) return onUpdate(formData);

    return onCreate(formData);
  }

  function onCreate(formData) {
    const req = {
      ...formData,
    };

    axios
      .post(`/organizations/`, req)
      .then(({ data }) => {
        onSuccess(data.response[0].id);
      })
      .then(() => setLoading(false))
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  function onUpdate(formData) {
    const req = {
      ...formData,
      organization_id: id,
    };

    axios
      .put(`/organizations/${id}/`, req)
      .then(() => setLoading(false))
      .then(() => onSuccess())
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const fields = [
    {
      name: "name",
      label: t("name"),
      initialValue: org?.name || "",
      required: true,
      schema: () => Yup.string().required(t("validation:required")),
    },
    {
      name: "shortname",
      label: "Shortname (for custom url...lowercase letters and numbers only)",
      initialValue: org?.shortname || "",
      required: true,
      schema: () => Yup.string().required("Required"),
    },
    {
      name: "address_1",
      label: t("address"),
      initialValue: org?.address_1 || "",
      required: false,
      schema: () => Yup.string(),
    },
    {
      name: "address_2",
      label: t("address_line_2"),
      initialValue: org?.address_2 || "",
      required: false,
      schema: () => null,
    },
    {
      name: "city",
      label: t("city"),
      initialValue: org?.city || "",
      required: false,
      schema: () => Yup.string(),
    },
    {
      name: "state_province",
      label: t("state"),
      initialValue: org?.state_province || "",
      required: false,
      select: true,
      search: true,
      options: usStates,
      schema: () => Yup.string(),
    },
    {
      name: "zip",
      label: t("zip"),
      initialValue: org?.zip || "",
      required: false,
      schema: () => Yup.string(),
    },
    {
      name: "country",
      label: t("country"),
      initialValue: org?.country || "",
      required: false,
      select: true,
      options: countries,
      schema: () => Yup.string().nullable().required(t("validation:required")),
    },
    {
      name: "target_industry",
      label: t("industry_singular"),
      initialValue: org?.target_industry || "",
      required: true,
      select: true,
      options: industries,
      schema: () => Yup.string().nullable().required(t("validation:required")),
    },
    {
      name: "hide_invites",
      label: "Hide Invites",
      initialValue: org?.hide_invites || "",
      required: true,
      select: true,
      options: [
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ],
      schema: () => Yup.string().nullable().required("Required"),
    },
    {
      name: "hol_company",
      label: "Heart of Leadership Company",
      initialValue: org?.hol_company || "",
      toggle: true,
      schema: () => null,
    },
    {
      name: "comp_assess_company",
      label: "Competency Assessment Company",
      initialValue: org?.comp_assess_company || "",
      toggle: true,
      schema: () => null,
    },
  ].filter((f) => (id ? true : ["name"].includes(f.name)));

  return (
    <div>
      <BasicForm
        loading={loading}
        onSubmit={onSubmit}
        fields={fields}
        buttonProps={{
          fullWidth: id ? false : true,
        }}
      />
    </div>
  );
}
